import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-semibold transition-all focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 active:scale-95",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        // Fills with the RAISED surface, not the page plane.
        //
        // `bg-background` meant the inside of an outline button was the exact
        // colour of the page behind it — a hairline border drawn around a hole
        // rather than a control sitting on the page. Worse in dark, where
        // --card and --background are both 5%, so the button matched every
        // surface it could possibly sit on.
        //
        // --card-inner is the token for "a raised thing on any surface":
        // #ffffff in light, 14% in dark. It lifts off the page plane AND off a
        // card, which is where most of these live. Apps that do not define it
        // (ping, webplayer, admin) fall back to --background and keep exactly
        // the look they have today.
        outline:
          "border border-[hsl(var(--border-button,var(--input)))] bg-[hsl(var(--card-inner,var(--background)))] hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
      },
      size: {
        default: "h-10 px-6 py-2.5",
        sm: "h-8 px-4 text-xs",
        lg: "h-12 px-10 text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        // Hooks for an app's own skin (e.g. Pocket's iPhone look on phones).
        data-ui="button" data-variant={variant ?? 'default'} data-size={size ?? 'default'}
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
